import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useWorkOrders,
  useCreateWorkOrder,
  useStartWorkOrder,
  useCompleteWorkOrder,
} from '../hooks/useWmsWorkOrders';
import { useDebounce } from '../hooks/useDebounce';
import { WorkOrdersFilters } from '../components/work-orders/WorkOrdersFilters';
import { WorkOrdersTable } from '../components/work-orders/WorkOrdersTable';
import { WorkOrderFormModal } from '../components/work-orders/WorkOrderFormModal';
import type {
  WorkOrder,
  WorkOrderStatus,
  WorkOrderPriority,
  CreateWorkOrderInput,
  UpdateWorkOrderInput,
  WorkOrdersResponse,
} from '../types';

export default function WorkOrdersPage() {
  const navigate = useNavigate();

  // Filter state
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [status, setStatus] = useState('all');
  const [priority, setPriority] = useState('all');
  const [supplierId, setSupplierId] = useState<string | undefined>();
  const [pageNumber, setPageNumber] = useState(0);
  const pageLength = 50;

  // Reset to first page when debounced search changes
  useEffect(() => {
    setPageNumber(0);
  }, [debouncedSearch]);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Queries
  const workOrdersQuery = useWorkOrders({
    search: debouncedSearch || undefined,
    status: status === 'all' ? undefined : (status as WorkOrderStatus),
    priority: priority === 'all' ? undefined : (priority as WorkOrderPriority),
    supplier_id: supplierId,
    page_number: pageNumber,
    page_length: pageLength,
  });

  // Mutations
  const createMutation = useCreateWorkOrder();
  const startMutation = useStartWorkOrder();
  const completeMutation = useCompleteWorkOrder();

  const workOrdersData = workOrdersQuery.data as WorkOrdersResponse | undefined;
  const isLoading = workOrdersQuery.isLoading;
  const error = workOrdersQuery.error;

  const workOrders = workOrdersData?.items || [];
  const total = workOrdersData?.total || 0;

  const handleCreate = async (data: CreateWorkOrderInput | UpdateWorkOrderInput) => {
    const result = await createMutation.mutateAsync(data as CreateWorkOrderInput);
    navigate(`/wms/work-orders/${result.id}`);
  };

  const handleRowClick = (wo: WorkOrder) => {
    navigate(`/wms/work-orders/${wo.id}`);
  };

  const handleStart = async (wo: WorkOrder) => {
    await startMutation.mutateAsync(wo.id);
  };

  const handleComplete = async (wo: WorkOrder) => {
    await completeMutation.mutateAsync(wo.id);
  };

  const totalPages = Math.ceil(total / pageLength);

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Work Orders</h1>
          <p className="text-muted-foreground">
            Manage fulfillment work orders
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>Create Work Order</Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <WorkOrdersFilters
            search={search}
            onSearchChange={setSearch}
            status={status}
            onStatusChange={(v) => {
              setStatus(v);
              setPageNumber(0);
            }}
            priority={priority}
            onPriorityChange={(v) => {
              setPriority(v);
              setPageNumber(0);
            }}
            supplierId={supplierId}
            onSupplierChange={(v) => {
              setSupplierId(v);
              setPageNumber(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent className="pt-6">
          {error ? (
            <div className="text-center py-8 text-destructive">
              Error loading work orders: {error.message}
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <>
              <WorkOrdersTable
                workOrders={workOrders}
                onRowClick={handleRowClick}
                onStart={handleStart}
                onComplete={handleComplete}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {pageNumber * pageLength + 1} to{' '}
                    {Math.min((pageNumber + 1) * pageLength, total)} of {total} work orders
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber((p) => Math.max(0, p - 1))}
                      disabled={pageNumber === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={pageNumber >= totalPages - 1}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <WorkOrderFormModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />
    </div>
  );
}
